import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { ReactiveDirective } from '@any2any/shared';
import { NgxScannerQrcodeComponent } from 'ngx-scanner-qrcode';
import { BehaviorSubject, delay } from 'rxjs';
import { WINDOW } from '../../core/consts/window.injection-token';
import { Size } from '../types/math';
import { QrcodeScanner } from './qrcode-scanner';

@Component({
    selector: 'app-qrcode-scanner',
    templateUrl: './qrcode-scanner.component.html',
    styleUrls: ['./qrcode-scanner.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class QrcodeScannerComponent extends ReactiveDirective implements AfterViewInit {

  @Input() public cameraSelector?: QrcodeScanner.CameraSelector;
  @Output() public dataScan = new EventEmitter<QrcodeScanner.Data>();
  @Output() public statusChange = new EventEmitter<QrcodeScanner.Status>();
  @Output() public videoSizeKnown = new EventEmitter<Size>();

  protected readonly window = inject<Window>(WINDOW);

  protected statusSubject = new BehaviorSubject<QrcodeScanner.Status>('initialization');
  protected readonly config: QrcodeScanner.Config = {
    constraints: {
      video: {
        aspectRatio: 1.7777777778,
        width: this.window.innerWidth,
      }
    }
  };
  protected readonly fancyCorners = QrcodeScanner.FANCY_SVG_CORNERS;

  @ViewChild('scanner') private readonly scanner!: NgxScannerQrcodeComponent;
  private shouldBeScanning = false;
  private selectedDeviceId: string|null = null;

  constructor() {
    super();
    this.connectStatusEvent();
  }

  public get status(): QrcodeScanner.Status {
    return this.statusSubject.value;
  }

  @Input()
  public set enabled(value: boolean | undefined) {
    this.shouldBeScanning = !!value;
    this.syncScanningStatus();
  }

  public ngAfterViewInit() {
    this.initialize();
  }

  protected get hasError(): boolean {
    return this.status === 'no_cameras' || this.status === 'no_permissions';
  }

  protected onScan(results: QrcodeScanner.Result[]): void {
    if (results?.length) {
      console.debug(`Successful scan, data length: ${results[0].value.length}`);
      this.dataScan.emit({ data: results[0].value });
    }
  }

  private connectStatusEvent(): void {
    this.statusSubject
      .asObservable()
      .pipe(this.observeUntilDestroy())
      .subscribe(status => this.statusChange.emit(status));
  }

  private initialize(): void {
    this.scanner.isReady
      .pipe(delay(1000))
      .subscribe(() => {
        console.debug('QR code scanner is ready');
        this.statusSubject.next('ready');
        this.syncScanningStatus();
      });
  }

  private syncScanningStatus(): void {
    if (this.status === 'initialization' || this.hasError) {
      return;
    }
    switch (this.status) {
      case 'ready':
        if (this.shouldBeScanning) {
          this.selectDeviceAndStart();
        }
        break;
      case 'paused':
        if (this.shouldBeScanning) {
          this.play();
        }
        break;
      case 'scanning':
        if (!this.shouldBeScanning) {
          this.pause();
        }
        break;
    }
  }

  private selectDeviceAndStart(): void {
    this.scanner.start(() => {
        // This callback has to be here in order to select device manually :)
      })
      .subscribe({
        next: (cameras: QrcodeScanner.Device[]) => {
          let useCamera: QrcodeScanner.Device;
          if (typeof this.cameraSelector === 'function') {
            useCamera = this.cameraSelector(cameras);
          } else {
            useCamera = cameras[0];
          }
          this.startDevice(useCamera);
        },
        error: (error: DOMException) => {
          switch (error.name) {
            case 'NotFoundError':
              this.statusSubject.next('no_cameras');
              console.debug(`No cameras available`);
              break;
            case 'NotAllowedError':
              this.statusSubject.next('no_permissions');
              console.debug(`Camera permissions not granted`);
              break;
          }
        }
      });
  }

  private startDevice(device: QrcodeScanner.Device): void {
    this.selectedDeviceId = device.deviceId;
    this.scanner.playDevice(device.deviceId)
      .subscribe(() => {
        this.statusSubject.next('scanning');
        const { videoWidth, videoHeight } = this.scanner.video.nativeElement;
        console.debug(`QR code scanner started camera "${device.label}", video dimensions: ${videoWidth} x ${videoHeight}`);
        this.videoSizeKnown.emit({ width: videoWidth, height: videoHeight });
      });
  }

  private play(): void {
    if (this.selectedDeviceId) {
      this.scanner.playDevice(this.selectedDeviceId)
        .subscribe(() => {
          this.statusSubject.next('scanning');
          console.debug('QR code scanner continues scanning');
        });
    } else {
      console.error('Cant play device if none selected first');
    }
  }

  private pause(): void {
    this.scanner.stop()
      .subscribe(() => {
        this.statusSubject.next('paused');
        console.debug('QR code scanner paused');
      });
  }

}
